import React, { useMemo, useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import {
  createDraftSignatureValue,
  formatSignedAt,
  getSignatureDisplayClassName,
  isSignatureSigned
} from '../utils/signatureFieldUtils';

const SignatureField = ({
  field,
  value,
  onDraftChange,
  onSign,
  onReset,
  readOnly = false,
  signing = false,
  allowReset = false
}) => {
  const [localError, setLocalError] = useState('');
  const consentItems = Array.isArray(field?.consentItems) ? field.consentItems : [];
  const displayStyle = field?.nameDisplayStyle === 'typed' ? 'typed' : 'cursive';
  const signed = isSignatureSigned(value);

  const draft = useMemo(() => createDraftSignatureValue(value), [value]);
  const draftConsents = draft.draftConsents || {};
  const draftSignerName = draft.draftSignerName || '';

  const setDraft = (patch) => {
    if (readOnly || signed) return;
    onDraftChange?.({
      status: 'unsigned',
      draftConsents: { ...draftConsents, ...(patch.draftConsents || {}) },
      draftSignerName: patch.draftSignerName !== undefined ? patch.draftSignerName : draftSignerName
    });
  };

  const handleSign = async () => {
    setLocalError('');
    const name = String(draftSignerName || '').trim();
    if (!name) {
      setLocalError('Enter your name before signing.');
      return;
    }
    const clientConsents = consentItems.map((item) => ({
      id: item.id,
      checked: Boolean(draftConsents[item.id])
    }));
    const missingRequired = consentItems.filter((item) => item.required !== false && !draftConsents[item.id]);
    if (missingRequired.length > 0) {
      setLocalError('Check all required agreements before signing.');
      return;
    }
    try {
      await onSign?.({ signerName: name, consents: clientConsents });
    } catch (e) {
      setLocalError(e?.response?.data?.error || e?.message || 'Failed to sign');
    }
  };

  if (signed) {
    const rendered = value.signatureDisplay?.renderedText || value.signerName || '';
    const style = value.signatureDisplay?.style || displayStyle;
    return (
      <div className="border rounded p-3 bg-light-subtle">
        <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
          <div className="text-muted small">Signed</div>
          {allowReset && !readOnly ? (
            <Button variant="link" size="sm" className="p-0 small" onClick={() => onReset?.()}>
              Reset
            </Button>
          ) : null}
        </div>
        <div className={`mb-2 ${getSignatureDisplayClassName(style)}`}>{rendered}</div>
        <div className="small text-muted">
          {formatSignedAt(value.signedAt || value.audit?.signedAt)}
        </div>
        {value.consents?.length > 0 && (
          <ul className="small mb-0 mt-2 ps-3">
            {value.consents.map((c) => (
              <li key={c.id}>
                <span className={c.checked ? 'text-success' : 'text-muted'}>
                  {c.checked ? '✓' : '○'}
                </span>{' '}
                {c.text}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div className="d-flex flex-column gap-3">
      {consentItems.map((item) => (
        <Form.Check
          key={item.id}
          type="checkbox"
          id={`${field.id}_${item.id}`}
          disabled={readOnly || signing}
          checked={Boolean(draftConsents[item.id])}
          onChange={(e) => setDraft({ draftConsents: { [item.id]: e.target.checked } })}
          label={<span className="small">{item.text || 'Agreement'}</span>}
        />
      ))}
      <Form.Group>
        <Form.Label className="small fw-semibold mb-1">
          {field.nameLabel || 'Type your full legal name'}
          {field.required ? <span className="text-danger"> *</span> : null}
        </Form.Label>
        <Form.Control
          type="text"
          value={draftSignerName}
          disabled={readOnly || signing}
          placeholder={field.namePlaceholder || 'First Last'}
          onChange={(e) => setDraft({ draftSignerName: e.target.value })}
        />
        {draftSignerName.trim() ? (
          <div className={`mt-2 p-2 border rounded bg-white ${getSignatureDisplayClassName(displayStyle)}`}>
            {draftSignerName.trim()}
          </div>
        ) : null}
      </Form.Group>
      {localError ? <Alert variant="danger" className="py-2 mb-0 small">{localError}</Alert> : null}
      {!readOnly && (
        <Button variant="primary" size="sm" className="align-self-start" disabled={signing} onClick={handleSign}>
          {signing ? 'Signing…' : 'Sign'}
        </Button>
      )}
    </div>
  );
};

export default SignatureField;
